import { supabase } from './supabase'
import { MainCategory } from '@flows.cash/types'
import { PostgrestError } from '@supabase/supabase-js'
import { ensureValidIcon } from '../utils/categoryIcons'
import { isValidUuid } from '../utils/categoryMigration'
import { v4 as uuidv4 } from 'uuid'

export class CategoryService {
  private static instance: CategoryService
  
  private constructor() {}
  
  public static getInstance(): CategoryService {
    if (!CategoryService.instance) {
      CategoryService.instance = new CategoryService()
    }
    return CategoryService.instance
  }

  async fetchCategories(userId: string) {
    try {
      // Optimized query with column selection
      const { data: categories, error } = await supabase
        .from('categories')
        .select(`
          id,
          name,
          type,
          color,
          icon,
          parent_id,
          created_at,
          updated_at
        `)
        .eq('user_id', userId)
        .order('created_at', { ascending: true })

      if (error) throw error

      // Fetch transaction counts for categories dynamically
      const { data: transactionCounts, error: countError } = await supabase
        .from('transactions')
        .select('category_id')
        .eq('user_id', userId)
        .not('category_id', 'is', null);

      if (countError) {
        console.warn('Error fetching transaction counts:', countError);
      }

      // Count transactions per category
      const categoryCountMap = new Map<string, number>();
      if (transactionCounts) {
        transactionCounts.forEach(transaction => {
          if (transaction.category_id) {
            const count = categoryCountMap.get(transaction.category_id) || 0;
            categoryCountMap.set(transaction.category_id, count + 1);
          }
        });
      }

      return {
        data: this.processCategories(categories || [], categoryCountMap),
        error: null
      }
    } catch (error) {
      console.error('Error fetching categories:', error)
      return {
        data: null,
        error: error as PostgrestError
      }
    }
  }

  async saveCategories(userId: string, categories: MainCategory[]) {
    try {
      // Prepare optimized batch insert/update
      const categoriesToSave = this.prepareCategoriesForSave(userId, categories)
      
      // Use chunking for large datasets
      const CHUNK_SIZE = 100
      for (let i = 0; i < categoriesToSave.length; i += CHUNK_SIZE) {
        const chunk = categoriesToSave.slice(i, i + CHUNK_SIZE)
        const { error: upsertError } = await supabase
          .from('categories')
          .upsert(chunk, { 
            onConflict: 'id',
            ignoreDuplicates: false
          })

        if (upsertError) throw upsertError
      }

      // Get all category IDs from the input to identify which ones to keep
      const categoryIds = new Set<string>();
      categories.forEach(category => {
        categoryIds.add(category.id);
        category.subcategories.forEach(subcategory => {
          categoryIds.add(subcategory.id);
        });
      });

      // Only attempt to delete if there are categories to keep
      if (categoryIds.size > 0) {
        // Convert the Set to an array
        const categoryIdsArray = Array.from(categoryIds);
        
        // Only attempt to delete categories that are no longer in the list
        // This will fail for categories referenced by transactions (which is fine)
        let query = supabase
          .from('categories')
          .delete()
          .eq('user_id', userId);
        
        // Only add the 'not in' filter if we have IDs to exclude
        if (categoryIdsArray.length > 0) {
          // For PostgreSQL, we need to format the array properly
          query = query.not('id', 'in', `(${categoryIdsArray.map(id => `'${id}'`).join(',')})`);
        }
        
        const { error: deleteError } = await query;

        // We don't throw on delete error since some categories might be referenced by transactions
        if (deleteError) {
          console.warn('Some categories could not be deleted due to foreign key constraints:', deleteError);
        }
      }

      return { error: null }
    } catch (error) {
      console.error('Error saving categories:', error)
      return { error: error as PostgrestError }
    }
  }

  async subscribeToCategories(userId: string, callback: (categories: MainCategory[]) => void) {
    return supabase
      .channel('categories_changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'categories',
          filter: `user_id=eq.${userId}`
        },
        async (payload) => {
          console.log('Category change detected:', payload.eventType);
          // Fetch fresh data when changes occur
          const { data } = await this.fetchCategories(userId)
          if (data) {
            callback(data)
          }
        }
      )
      .subscribe()
  }

  private processCategories(categories: any[], categoryCountMap?: Map<string, number>): MainCategory[] {
    const categoryMap: Record<string, any> = {}
    const mainCategories: MainCategory[] = []

    categories.forEach(category => {
      category.icon = ensureValidIcon(category)
      categoryMap[category.id] = category
    })

    categories.forEach(category => {
      if (!category.parent_id) {
        mainCategories.push({
          id: category.id,
          name: category.name,
          type: category.type,
          color: category.color,
          icon: category.icon,
          isExpanded: false,
          subcategories: []
        })
      }
    })

    categories.forEach(category => {
      if (category.parent_id) {
        const mainCategory = mainCategories.find(mc => mc.id === category.parent_id)
        if (mainCategory) {
          const transactionCount = categoryCountMap ? (categoryCountMap.get(category.id) || 0) : (category.transaction_count || 0);
          mainCategory.subcategories.push({
            id: category.id,
            name: category.name,
            type: category.type,
            color: category.color,
            icon: category.icon,
            parentId: category.parent_id,
            transactionCount: transactionCount,
            description: category.description,
            trackInNetWorth: category.track_in_net_worth,
            isRecurring: category.is_recurring
          })
        }
      }
    })

    return mainCategories
  }

  private prepareCategoriesForSave(userId: string, categories: MainCategory[]) {
    const categoriesToSave: any[] = []
    const timestamp = new Date().toISOString()

    categories.forEach(category => {
      // Add main category
      categoriesToSave.push({
        id: category.id && isValidUuid(category.id) ? category.id : uuidv4(),
        user_id: userId,
        name: category.name,
        type: category.type,
        color: category.color,
        icon: category.icon,
        parent_id: null,
        description: category.description,
        track_in_net_worth: category.trackInNetWorth ?? true,
        is_recurring: category.isRecurring ?? false,
        created_at: timestamp,
        updated_at: timestamp
      })

      // Add subcategories
      category.subcategories.forEach(subcategory => {
        categoriesToSave.push({
          id: subcategory.id && isValidUuid(subcategory.id) ? subcategory.id : uuidv4(),
          user_id: userId,
          name: subcategory.name,
          type: subcategory.type,
          color: subcategory.color,
          icon: subcategory.icon,
          parent_id: category.id,
          description: subcategory.description,
          track_in_net_worth: subcategory.trackInNetWorth ?? true,
          is_recurring: subcategory.isRecurring ?? false,
          created_at: timestamp,
          updated_at: timestamp
        })
      })
    })

    return categoriesToSave
  }

  // Add a new function for adding a single category
  async addCategory(userId: string, category: MainCategory) {
    try {
      // Prepare the category for saving
      const categoryToSave = {
        id: category.id && isValidUuid(category.id) ? category.id : uuidv4(),
        user_id: userId,
        name: category.name,
        type: category.type,
        color: category.color,
        icon: category.icon,
        parent_id: null,
        description: category.description,
        track_in_net_worth: category.trackInNetWorth ?? true,
        is_recurring: category.isRecurring ?? false,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };
      
      // Insert the main category
      const { error: insertError } = await supabase
        .from('categories')
        .insert(categoryToSave);

      if (insertError) throw insertError;
      
      // Insert subcategories if any
      if (category.subcategories && category.subcategories.length > 0) {
        const subcategoriesToSave = category.subcategories.map(subcategory => ({
          id: subcategory.id && isValidUuid(subcategory.id) ? subcategory.id : uuidv4(),
          user_id: userId,
          name: subcategory.name,
          type: subcategory.type,
          color: subcategory.color,
          icon: subcategory.icon,
          parent_id: categoryToSave.id,
          description: subcategory.description,
          track_in_net_worth: subcategory.trackInNetWorth ?? true,
          is_recurring: subcategory.isRecurring ?? false,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        }));
        
        const { error: subInsertError } = await supabase
          .from('categories')
          .insert(subcategoriesToSave);
          
        if (subInsertError) throw subInsertError;
      }
      
      return { error: null };
    } catch (error) {
      console.error('Error adding category:', error);
      return { error: error as PostgrestError };
    }
  }
  
  // Add a new function for adding a subcategory
  async addSubcategory(userId: string, parentId: string, subcategory: any) {
    try {
      // Prepare the subcategory for saving
      const subcategoryToSave = {
        id: subcategory.id && isValidUuid(subcategory.id) ? subcategory.id : uuidv4(),
        user_id: userId,
        name: subcategory.name,
        type: subcategory.type,
        color: subcategory.color,
        icon: subcategory.icon,
        parent_id: parentId,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };
      
      // Insert the subcategory
      const { error: insertError } = await supabase
        .from('categories')
        .insert(subcategoryToSave);

      if (insertError) throw insertError;
      
      return { error: null };
    } catch (error) {
      console.error('Error adding subcategory:', error);
      return { error: error as PostgrestError };
    }
  }
  
  // Update a category without affecting others
  async updateCategory(userId: string, category: any) {
    try {
      // Prepare the category for updating
      const categoryToUpdate = {
        id: category.id,
        user_id: userId,
        name: category.name,
        type: category.type,
        color: category.color,
        icon: category.icon,
        // Handle both camelCase (parentId) and snake_case (parent_id) property names
        parent_id: category.parentId || category.parent_id || null,
        description: category.description,
        track_in_net_worth: category.trackInNetWorth ?? true,
        is_recurring: category.isRecurring ?? false,
        updated_at: new Date().toISOString()
      };
      
      // Update the category
      const { error: updateError } = await supabase
        .from('categories')
        .update(categoryToUpdate)
        .eq('id', category.id)
        .eq('user_id', userId);

      if (updateError) throw updateError;
      
      return { error: null };
    } catch (error) {
      console.error('Error updating category:', error);
      return { error: error as PostgrestError };
    }
  }
  
  // Delete a category if it's not referenced by transactions
  async deleteCategory(userId: string, categoryId: string) {
    try {
      // First check if this is a main category with subcategories
      const { data: subcategories, error: fetchError } = await supabase
        .from('categories')
        .select('id')
        .eq('parent_id', categoryId)
        .eq('user_id', userId);
        
      if (fetchError) throw fetchError;
      
      // Delete any subcategories first (if they exist)
      if (subcategories && subcategories.length > 0) {
        const subcategoryIds = subcategories.map(sub => sub.id);
        
        // Delete all subcategories in one operation
        const { error: deleteSubsError } = await supabase
          .from('categories')
          .delete()
          .in('id', subcategoryIds)
          .eq('user_id', userId);
          
        if (deleteSubsError) {
          // If subcategories are used in transactions, we can't delete them
          if (deleteSubsError.code === '23503') {
            return { 
              error: deleteSubsError,
              message: 'Cannot delete subcategories that are used in transactions. Please reassign those transactions first.'
            };
          }
          throw deleteSubsError;
        }
      }
      
      // Then delete the main category
      const { error: deleteError } = await supabase
        .from('categories')
        .delete()
        .eq('id', categoryId)
        .eq('user_id', userId);

      if (deleteError) {
        // Check if it's a foreign key constraint error
        if (deleteError.code === '23503') {
          return { 
            error: deleteError,
            message: 'Cannot delete a category that is used in transactions. Please reassign those transactions first.'
          };
        }
        throw deleteError;
      }
      
      return { error: null };
    } catch (error) {
      console.error('Error deleting category:', error);
      return { error: error as PostgrestError };
    }
  }
} 